import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, Command, GraduationCap, Menu, ScrollText, Search, X } from 'lucide-react';
import { allLessons, courses, lessonText, totalLessons, STEP_COUNT } from '../content';
import { useProgress } from '../hooks/useProgress';
import Icon from './Icon';

function Logo() {
  return (
    <Link to="/" className="logo" aria-label="Learn Kamailio home">
      <span className="logo-mark" aria-hidden="true">
        <svg viewBox="0 0 32 32" width="30" height="30">
          <defs>
            <linearGradient id="lg1" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#ff3d7f" />
              <stop offset="0.5" stopColor="#ffcf3d" />
              <stop offset="1" stopColor="#00e5ff" />
            </linearGradient>
          </defs>
          <rect x="1" y="1" width="30" height="30" rx="9" fill="url(#lg1)" />
          <path d="M9 9v14M9 16l8-7M12.5 13l5.5 10" stroke="#120f2b" strokeWidth="3" strokeLinecap="round" fill="none" />
          <circle cx="23" cy="10" r="2.4" fill="#120f2b" />
        </svg>
      </span>
      <span className="logo-text">Learn<b>Kamailio</b></span>
    </Link>
  );
}

function SearchPalette({ open, onClose }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const index = useMemo(
    () => allLessons.map((x) => ({ ...x, hay: `${x.lesson.title} ${x.lesson.summary} ${lessonText(x.lesson)}`.toLowerCase() })),
    []
  );
  const results = useMemo(() => {
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return index.slice(0, 8);
    return index
      .map((x) => {
        const title = x.lesson.title.toLowerCase();
        let s = 0;
        for (const t of terms) {
          if (!x.hay.includes(t)) return null;
          s += title.includes(t) ? 10 : 1;
        }
        return { ...x, s };
      })
      .filter(Boolean)
      .sort((a, b) => b.s - a.s)
      .slice(0, 10);
  }, [q, index]);

  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    }
  }, [open]);

  const go = (r) => {
    navigate(`/course/${r.course.id}/${r.lesson.id}`);
    onClose();
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter' && results[active]) go(results[active]);
    if (e.key === 'Escape') onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="palette-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div
            className="palette"
            role="dialog"
            aria-label="Search lessons"
            initial={{ y: -20, scale: 0.97, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="palette-input">
              <Search size={18} />
              <input
                id="lesson-search"
                ref={inputRef}
                value={q}
                onChange={(e) => { setQ(e.target.value); setActive(0); }}
                onKeyDown={onKey}
                placeholder="Search lessons: t_relay, NAT, dispatcher, 407..."
                autoComplete="off"
              />
              <kbd>Esc</kbd>
            </div>
            <ul className="palette-results">
              {results.length === 0 && <li className="palette-empty">No lessons mention “{q}”.</li>}
              {results.map((r, i) => (
                <li key={`${r.course.id}/${r.lesson.id}`}>
                  <button
                    type="button"
                    className={i === active ? 'is-active' : ''}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(r)}
                    style={{ '--c1': r.course.colors[0], '--c2': r.course.colors[1] }}
                  >
                    <span className="palette-icon"><Icon name={r.lesson.icon} size={16} /></span>
                    <span className="palette-text">
                      <strong>{r.lesson.title}</strong>
                      <small>Step {r.course.step} · {r.course.title}</small>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function Nav() {
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const { doneCount } = useProgress();
  const location = useLocation();
  const pct = Math.round((doneCount / totalLessons) * 100);

  useEffect(() => setMenu(false), [location.pathname]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearch((s) => !s);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <header className="nav">
        <div className="nav-inner">
          <Logo />
          <nav className={`nav-links ${menu ? 'is-open' : ''}`} aria-label="Main">
            <NavLink to="/" end><GraduationCap size={17} /> Steps</NavLink>
            <NavLink to="/courses"><BookOpen size={17} /> Courses</NavLink>
            <NavLink to="/cheatsheet"><ScrollText size={17} /> Cheat sheet</NavLink>
            <div className="nav-courses">
              {courses.map((c) => (
                <NavLink key={c.id} to={`/course/${c.id}`} style={{ '--c1': c.colors[0], '--c2': c.colors[1] }}>
                  <span className="nav-step">{c.step}</span> {c.title}
                </NavLink>
              ))}
            </div>
          </nav>
          <div className="nav-tools">
            <button type="button" className="search-btn" onClick={() => setSearch(true)} aria-label="Search lessons">
              <Search size={16} />
              <span>Search</span>
              <kbd><Command size={11} />K</kbd>
            </button>
            <div className="nav-progress" title={`${doneCount} of ${totalLessons} lessons complete`}>
              <svg viewBox="0 0 36 36" width="34" height="34" aria-hidden="true">
                <circle cx="18" cy="18" r="15" className="ring-bg" />
                <circle cx="18" cy="18" r="15" className="ring-fg" strokeDasharray={`${(pct / 100) * 94.2} 94.2`} />
              </svg>
              <span>{pct}%</span>
            </div>
            <button type="button" className="menu-btn" onClick={() => setMenu((m) => !m)} aria-label="Menu" aria-expanded={menu}>
              {menu ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>
      <SearchPalette open={search} onClose={() => setSearch(false)} />
    </>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div>
          <Logo />
          <p>A free, hands-on path from your first SIP message to a production Kamailio edge, in {STEP_COUNT} steps.</p>
        </div>
        <div className="footer-cols">
          <div>
            <strong>Steps</strong>
            {courses.map((c) => <Link key={c.id} to={`/course/${c.id}`}>{c.step}. {c.title}</Link>)}
          </div>
          <div>
            <strong>Official resources</strong>
            <a href="https://www.kamailio.org/" target="_blank" rel="noreferrer">kamailio.org</a>
            <a href="https://www.kamailio.org/docs/modules/stable/" target="_blank" rel="noreferrer">Module documentation</a>
            <a href="https://github.com/kamailio/kamailio" target="_blank" rel="noreferrer">Kamailio on GitHub</a>
            <a href="https://www.rfc-editor.org/rfc/rfc3261" target="_blank" rel="noreferrer">RFC 3261 (SIP)</a>
            <a href="https://github.com/sipwise/rtpengine" target="_blank" rel="noreferrer">RTPEngine</a>
          </div>
        </div>
      </div>
      <p className="footer-note">Independent learning material. Kamailio is an open source project; check the official docs for your exact version.</p>
    </footer>
  );
}
